/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useEffect, useState, useRef } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { createLocalStorageProvider } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/index.tsx';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';

import {
	setUserProperties,
	getUserProperty,
} from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { AnalyticsEventType } from './types.tsx';

const AD_CONTROLS_FETCH_ERROR = 'adControlsFetchError';

const fireAdControlsError = (
	error: unknown,
	operation: 'get' | 'set',
	userPropertyKey: string,
	analytics: { packageName: string; teamName: string },
) => {
	const errorMessage = `Failed to ${operation} user property: ${userPropertyKey}`;
	const wrappedError = new Error(errorMessage, {
		cause: error instanceof Error ? error : undefined,
	});

	fireErrorAnalytics({
		meta: {
			id: AD_CONTROLS_FETCH_ERROR,
			packageName: analytics.packageName,
			teamName: analytics.teamName,
		},
		error: wrappedError,
	});
};

// Centralized analytics function
export const useAdControlsAnalytics = (): ((
	eventType: AnalyticsEventType,
	attributes?: AnalyticsEventPayload['attributes'],
	actionSubject?: string,
	actionSubjectId?: string,
) => void) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		(
			eventType: AnalyticsEventType,
			attributes: AnalyticsEventPayload['attributes'] = {},
			actionSubject?: string,
			actionSubjectId?: string,
		) => {
			const baseEvent = createAnalyticsEvent({
				action: eventType === 'viewed' ? 'viewed' : 'clicked',
				actionSubject: actionSubject || 'adControl',
				actionSubjectId: actionSubjectId || 'jira-ad-controls',
			});

			fireUIAnalytics(baseEvent, eventType, attributes);
		},
		[createAnalyticsEvent],
	);
};

type AdControlsFunctionality = {
	isPending: boolean;
	isDismissed: boolean;
	dismiss: () => Promise<void>;
};

type AdControlsOptions = {
	userPropertyKey: string;
	analytics: {
		packageName: string;
		teamName: string;
		panelName: string;
	};
	fireDefaultEvent?: boolean;
	onDismiss?: () => void;
};

export const useAdControlsFunctionality = ({
	onDismiss,
	userPropertyKey,
	analytics,
	fireDefaultEvent = true,
}: AdControlsOptions): AdControlsFunctionality => {
	const accountId = useAccountId() || '';
	const fireAdControlsAnalytics = useAdControlsAnalytics();

	const internalLocalStorageKey = `jira-${userPropertyKey.split('.')[0]}`;

	// Use refs for mutable variables to avoid linter warnings
	const localCache = useRef(createLocalStorageProvider(internalLocalStorageKey));
	const isDismissedValueRef = useRef(localCache.current.get(userPropertyKey) === 'true');
	const isDismissedLCSetRef = useRef(
		localCache.current.get(userPropertyKey) !== null &&
			localCache.current.get(userPropertyKey) !== undefined,
	);

	// isPending is used to indicate that the ad controls are still fetching the user property
	const [isPending, setIsPending] = useState(!isDismissedLCSetRef.current);
	const [isDismissed, setIsDismissed] = useState(isDismissedValueRef.current);

	// Helper function to update all dismiss-related state consistently
	const updateDismissState = useCallback(
		(dismissed: boolean) => {
			isDismissedValueRef.current = dismissed;
			setIsDismissed(dismissed);
			// @browser-storage setter
			localCache.current.set(userPropertyKey, String(dismissed));
			isDismissedLCSetRef.current = true;
		},
		[userPropertyKey],
	);

	const fetchUserProperties = useCallback(async () => {
		if (!accountId) {
			setIsPending(false);

			// Track missing account ID
			fireAdControlsAnalytics('missingAccountId', {
				panelName: analytics.panelName,
			});
			return;
		}

		try {
			setIsPending(true);
			const dismissedValue = await getUserProperty<boolean>(accountId, userPropertyKey);
			updateDismissState(dismissedValue);

			// Track successful fetch
			fireAdControlsAnalytics('fetchSuccess', {
				panelName: analytics.panelName,
				isDismissed: dismissedValue,
			});
		} catch (error) {
			if (error && typeof error === 'object' && 'statusCode' in error && error.statusCode === 404) {
				updateDismissState(false);
			} else {
				fireAdControlsError(error, 'get', userPropertyKey, analytics);
				// Keep current state on error - don't update dismiss state
			}
		} finally {
			setIsPending(false);
		}
	}, [accountId, userPropertyKey, analytics, updateDismissState, fireAdControlsAnalytics]);

	useEffect(() => {
		if (!isDismissedLCSetRef.current) {
			fetchUserProperties();
		}
	}, [fetchUserProperties]);

	const dismiss = useCallback(async () => {
		if (!accountId) {
			fireAdControlsError(
				new Error('Cannot dismiss ad controls without account ID'),
				'set',
				userPropertyKey,
				analytics,
			);
			return;
		}

		// Optimistically update all dismiss state
		updateDismissState(true);

		try {
			await setUserProperties(accountId, userPropertyKey, 'true');
			if (fireDefaultEvent) {
				fireAdControlsAnalytics('notInterested', {
					panelName: analytics.panelName,
				});
			}
		} catch (error) {
			// Keep optimistic update - user has indicated they want to dismiss
			// Only log the error for monitoring purposes
			fireAdControlsError(error, 'set', userPropertyKey, analytics);
		} finally {
			onDismiss?.();
		}
	}, [
		accountId,
		updateDismissState,
		userPropertyKey,
		analytics,
		fireDefaultEvent,
		fireAdControlsAnalytics,
		onDismiss,
	]);

	return { isDismissed, dismiss, isPending };
};
